import React, { useState } from 'react';
import { PatientFormData } from '@/types/forms';
import { useFormValidation } from '@/hooks/useFormValidation';
import { FormInput } from './FormComponents';
import { useToasts } from '@/state/ToastContext';
import { successToast, errorToast } from '@/utils/toastHelpers';

interface PatientFormProps {
  onSubmit: (data: PatientFormData) => Promise<void> | void;
  initialData?: PatientFormData;
}

const PatientForm: React.FC<PatientFormProps> = ({ onSubmit, initialData }) => {
  const [name, setName] = useState(initialData?.name ?? '');
  const [age, setAge] = useState(initialData?.age ?? '');
  const [species, setSpecies] = useState(initialData?.species ?? '');
  const [ownerName, setOwnerName] = useState(initialData?.ownerName ?? '');
  const [contact, setContact] = useState(initialData?.contact ?? '');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const { pushToast } = useToasts();

  const { validateForm, handleBlur, handleChange, getError, hasError, clearAllErrors } = useFormValidation({
    name: { 
      required: true, 
      minLength: 2,
      maxLength: 50,
      message: '病患姓名需要 2-50 個字元' 
    },
    age: { 
      required: true,
      custom: (value) => {
        const num = Number(value);
        if (isNaN(num) || num < 0 || num > 50) {
          return '年齡必須在 0-50 歲之間';
        }
        return null;
      }
    },
    species: { 
      required: true,
      minLength: 1,
      maxLength: 30,
      message: '請輸入物種（例如：犬、貓）'
    },
    ownerName: { 
      required: true,
      minLength: 2,
      maxLength: 50,
      message: '照護人姓名需要 2-50 個字元'
    },
    contact: { 
      required: true,
      phone: true,
      message: '請輸入有效的電話號碼'
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    const formData = { name, age, species, ownerName, contact };
    
    const validationResult = validateForm(formData);
    if (!validationResult.isValid) {
      const firstInvalidField = Object.keys(validationResult.errors)[0];
      if (firstInvalidField) {
        const fieldElement = document.getElementById(firstInvalidField) as HTMLElement | null;
        fieldElement?.focus();
      }
      setSubmitErrors(Object.values(validationResult.errors));
      pushToast(errorToast({
        message: '表單驗證失敗',
        detail: '請檢查並修正標示錯誤的欄位',
      }));
      return;
    }
    setSubmitErrors([]);

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      // Clear form on success
      setName('');
      setAge('');
      setSpecies('');
      setOwnerName('');
      setContact('');
      clearAllErrors();
      
      pushToast(successToast({
        message: '新增病患成功',
        detail: `${name} 的資料已成功建立`,
      }));
    } catch (error) {
      pushToast(errorToast({
        message: '新增病患失敗',
        detail: error instanceof Error ? error.message : '請稍後再試',
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setName('');
    setAge('');
    setSpecies('');
    setOwnerName('');
    setContact('');
    clearAllErrors();
    setSubmitErrors([]);
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit} noValidate>
      {submitErrors.length > 0 && (
        <div
          className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
          role="alert"
        >
          <p className="font-semibold">請修正以下欄位：</p>
          <ul className="mt-1 list-disc pl-5">
            {submitErrors.map((message, index) => (
              <li key={`${message}-${index}`}>{message}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <FormInput
          label="病患姓名"
          name="name"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            handleChange('name', e.target.value);
          }}
          onBlur={(e) => handleBlur('name', e.target.value)}
          error={getError('name')}
          touched={hasError('name')}
          success
          required
        />
        
        <FormInput
          label="年齡（歲）"
          name="age"
          type="number"
          value={age}
          onChange={(e) => {
            setAge(e.target.value);
            handleChange('age', e.target.value);
          }}
          onBlur={(e) => handleBlur('age', e.target.value)}
          error={getError('age')}
          touched={hasError('age')}
          success
          min="0"
          max="50"
          required
        />
      </div>

      <FormInput
        label="物種"
        name="species"
        type="text"
        value={species}
        onChange={(e) => {
          setSpecies(e.target.value);
          handleChange('species', e.target.value);
        }}
        onBlur={(e) => handleBlur('species', e.target.value)}
        error={getError('species')}
        touched={hasError('species')}
        success
        placeholder="犬、貓、特殊寵物…"
        helpText="請輸入寵物的種類"
        required
      />

      <div className="grid gap-4 md:grid-cols-2">
        <FormInput
          label="照護人姓名"
          name="ownerName"
          type="text"
          value={ownerName}
          onChange={(e) => {
            setOwnerName(e.target.value);
            handleChange('ownerName', e.target.value);
          }}
          onBlur={(e) => handleBlur('ownerName', e.target.value)}
          error={getError('ownerName')}
          touched={hasError('ownerName')}
          success
          required
        />
        
        <FormInput
          label="聯絡電話"
          name="contact"
          type="tel"
          value={contact}
          onChange={(e) => {
            setContact(e.target.value);
            handleChange('contact', e.target.value);
          }}
          onBlur={(e) => handleBlur('contact', e.target.value)}
          error={getError('contact')}
          touched={hasError('contact')}
          success
          placeholder="09xx-xxxxxx"
          helpText="請輸入可聯繫的電話號碼"
          required
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
          onClick={handleReset}
        >
          清除
        </button>
        <button
          type="submit"
          className="rounded-xl bg-brand-blue px-5 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-blue/90 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? '儲存中…' : '儲存病患'}
        </button>
      </div>
    </form>
  );
};

export default PatientForm;
